/**
 * Team alerts (WS-F, workspace docs/guardian/06, 2026-10-23): NWS fire-weather
 * warnings and active-fire detections intersecting the department's watched
 * areas, newest first. Every member reads the feed; each member chooses whether
 * to also get e-mail and can mute the feed. All alert text comes from the
 * Backend / NWS / WFIGS / FIRMS and is rendered as text only; a "View source"
 * link is shown only when the alert carries an absolute https URL. Pagination
 * follows the Backend's opaque `nextCursor`. A 503 `org_unavailable` hides the
 * section.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import {
  fetchDepartmentAlertPreferences,
  fetchDepartmentAlerts,
  updateDepartmentAlertPreferences,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';
import {
  alertSourceLabel,
  formatDeptTime,
  markAlertsSeen,
  newestAlertAt,
  safeAlertUrl,
} from '../utils/departmentOrg';

const PAGE = 50;

function AlertRow({ alert }) {
  const url = safeAlertUrl(alert.url);
  return (
    <Paper variant="outlined" component="li" sx={{ p: 1.5, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{alert.event}</Typography>
        <Chip size="small" variant="outlined" label={alertSourceLabel(alert.source)} />
        {alert.severity && <Chip size="small" variant="outlined" label={alert.severity} />}
      </Box>
      {alert.headline && <Typography variant="body2">{alert.headline}</Typography>}
      {alert.areaDesc && <Typography variant="caption" color="text.secondary">{alert.areaDesc}</Typography>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'baseline' }}>
        {formatDeptTime(alert.detectedAt) && (
          <Typography variant="caption" color="text.secondary">Detected {formatDeptTime(alert.detectedAt)}</Typography>
        )}
        {formatDeptTime(alert.onset) && (
          <Typography variant="caption" color="text.secondary">· Onset {formatDeptTime(alert.onset)}</Typography>
        )}
        {formatDeptTime(alert.expires) && (
          <Typography variant="caption" color="text.secondary">· Expires {formatDeptTime(alert.expires)}</Typography>
        )}
      </Box>
      {url && (
        <Link href={url} target="_blank" rel="noopener noreferrer nofollow" variant="caption">View source</Link>
      )}
    </Paper>
  );
}

function Preferences({ authToken }) {
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [off, setOff] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    (async () => {
      if (!authToken) return;
      const res = await fetchDepartmentAlertPreferences(authToken);
      if (!alive.current) return;
      if (res?.success) setPrefs({ email: Boolean(res.data?.email), muted: Boolean(res.data?.muted) });
      else if (res?.data?.code === 'org_unavailable') setOff(true);
      else setError(departmentErrorMessage(res, 'Could not load your alert preferences.'));
    })();
  }, [authToken]);

  const save = async (next) => {
    setBusy(true);
    setError(null);
    const res = await updateDepartmentAlertPreferences(authToken, next);
    setBusy(false);
    if (!alive.current) return;
    if (res?.success) setPrefs({ email: Boolean(res.data?.email), muted: Boolean(res.data?.muted) });
    else setError(departmentErrorMessage(res, 'Could not save your alert preferences.'));
  };

  if (off || !prefs) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <FormControlLabel
          control={<Switch size="small" checked={prefs.email} disabled={busy} onChange={(e) => save({ email: e.target.checked, muted: prefs.muted })} />}
          label="E-mail me alerts"
        />
        <FormControlLabel
          control={<Switch size="small" checked={prefs.muted} disabled={busy} onChange={(e) => save({ email: prefs.email, muted: e.target.checked })} />}
          label="Mute alerts"
        />
      </Box>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {string|null} [props.currentUserId] - Marks alerts seen (clears the badge).
 */
export default function DepartmentAlerts({ authToken, currentUserId = null }) {
  const [alerts, setAlerts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    if (!authToken) return;
    setStatus('loading');
    const res = await fetchDepartmentAlerts(authToken, { limit: PAGE });
    if (!alive.current) return;
    if (res?.success) {
      const list = Array.isArray(res.data?.alerts) ? res.data.alerts : [];
      setAlerts(list);
      setCursor(res.data?.nextCursor || null);
      setStatus('ready');
      markAlertsSeen(currentUserId, newestAlertAt(list));
    } else if (res?.data?.code === 'org_unavailable') {
      setOff(true);
    } else {
      setStatus('error');
      setError(departmentErrorMessage(res, 'Could not load alerts.'));
    }
  }, [authToken, currentUserId]);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    const res = await fetchDepartmentAlerts(authToken, { cursor, limit: PAGE });
    setLoadingMore(false);
    if (!alive.current) return;
    if (res?.success) {
      const more = Array.isArray(res.data?.alerts) ? res.data.alerts : [];
      setAlerts((prev) => [...prev, ...more]);
      setCursor(res.data?.nextCursor || null);
    } else {
      setError(departmentErrorMessage(res, 'Could not load more alerts.'));
    }
  };

  if (off) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Alerts</Typography>
      <Typography variant="body2" color="text.secondary">
        Fire-weather warnings and active-fire detections for your department&apos;s watched areas, newest first.
      </Typography>

      <Preferences authToken={authToken} />

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading alerts…</Typography>
        </Box>
      )}

      {status === 'ready' && (
        alerts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No alerts for your watched areas.</Typography>
        ) : (
          <>
            <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {alerts.map((a) => <AlertRow key={a.id} alert={a} />)}
            </Box>
            {cursor && (
              <Box>
                <Button size="small" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load older alerts'}
                </Button>
              </Box>
            )}
          </>
        )
      )}
    </Paper>
  );
}
